import { Locale, Localization } from '@economic/taco';
import { t, plural } from '@lingui/macro';

export const fetchLocalisation = (locale: Locale, dateFormat: string): Localization => {
    return {
        formatting: {
            date: dateFormat,
        },
        locale,
        texts: {
            banner: {
                close: t`Close`,
            },
            calendar: {
                months: [
                    t`January`,
                    t`February`,
                    t`March`,
                    t`April`,
                    t`May`,
                    t`June`,
                    t`July`,
                    t`August`,
                    t`September`,
                    t`October`,
                    t`November`,
                    t`December`,
                ],
                weekdaysShort: [t`Sun`, t`Mon`, t`Tue`, t`Wed`, t`Thu`, t`Fri`, t`Sat`],
                weekdays: [t`Sunday`, t`Monday`, t`Tuesday`, t`Wednesday`, t`Thursday`, t`Friday`, t`Saturday`],
                actions: {
                    previousMonth: t`Previous month`,
                    nextMonth: t`Next month`,
                    previousYear: t`Previous year`,
                    nextYear: t`Next year`,
                    today: t`Today's date`,
                },
            },
            combobox: {
                tooltip: t`Search in list`,
            },
            datepicker: {
                calendar: t`Calendar`,
                clear: t`Clear`,
                expand: t`Show calendar`,
                shortcuts: t`Shortcuts`,
            },
            dialog: {
                drag: t`Drag`,
                close: t`Close`,
            },
            drawer: {
                close: t`Close`,
            },
            hanger: {
                close: t`Close`,
            },
            header: {
                addAgreement: t`Add agreement`,
                logout: t`Log off`,
                more: t`More`,
                new: t`New`,
                search: t`Search for agreement...`,
            },
            listbox: {
                loading: t`Loading...`,
                empty: t`No results`,
                allOption: t`All`,
            },
            pagination: {
                label: t`Pagination`,
                showingXofYofTotal: t`Showing [X]-[Y] of [total] items`,
                pageSize: t`Items per page`,
                actions: {
                    firstPage: t`First page`,
                    firstPageWithShortcut: t`First page (Home)`,
                    nextPage: t`Next page`,
                    nextPageWithShortcut: t`Next page (Page Down)`,
                    previousPage: t`Previous page`,
                    previousPageWithShortcut: t`Previous page (Page Up)`,
                    lastPage: t`Last page`,
                    lastPageWithShortcut: t`Last page (End)`,
                    pageX: t`Go to page [X]`,
                },
            },
            searchInput: {
                button: t`Search`,
                clear: t`Clear`,
                placeholder: t`Search...`,
                findNext: t`Find next`,
                findPrevious: t`Find previous`,
            },
            select: {
                allOptionsSelected: t`All`,
            },
            select2: {
                allSelect: t`Select all`,
                allDeselect: t`Deselect all`,
                cancel: t`Cancel`,
                chooseColor: t`Pick a color`,
                create: t`Create`,
                delete: t`Delete`,
                save: t`Save`,
                search: t`Search...`,
                searchOrCreate: t`Search or create...`,
            },
            table: {
                copy: t`Copy (c)`,
                edit: t`Edit (e)`,
                del: t`Delete (del)`,
                newSubRow: t`New sub row`,
                actions: t`Other actions`,
                loading: t`Loading...`,
            },
            table2: {
                columns: {
                    actions: {
                        tooltip: t`Other actions`,
                    },
                    drag: {
                        tooltip: t`Drag row`,
                    },
                    expansion: {
                        collapse: t`Collapse row`,
                        collapseAll: t`Collapse all rows`,
                        expand: t`Expand row`,
                        expandAll: t`Expand all rows`,
                    },
                    menu: {
                        freezeColumns: count =>
                            plural(count, {
                                one: 'Freeze the first column',
                                other: 'Freeze first # columns',
                            }),
                        unfreezeColumns: t`Unfreeze all columns`,
                        hideColumn: t`Hide column`,
                    },
                    resize: {
                        tooltip: t`Resize column`,
                    },
                    select: {
                        deselect: t`Deselect row`,
                        deselectAll: t`Deselect all rows`,
                        select: t`Select row`,
                        selectAll: t`Select all rows`,
                    },
                },
                columnSettings: {
                    search: t`Search column...`,
                    tooltip: t`Column settings`,
                    button: t`Columns`,
                    noResults: t`No results`,
                },
                editing: {
                    button: t`Edit`,
                    tooltip: t`Edit`,
                    tooltipDisabled: t`There are no visible columns that support editing`,
                    rowIndicator: {
                        rowWillMove: t`Row will move due to sorting`,
                        rowWillBeHidden: t`Row will hide due to filtering`,
                        rowWillMoveReasonSearch: t`Due to a search filter applied to this table, this row will be hidden as soon as you select another row`,
                        rowWillMoveReasonFilter: t`Due to a filter applied to the column '[COLUMN]', this row will be hidden as soon as you select another row`,
                        rowWillMoveReasonSorting: t`Due to sorting applied to the column '[COLUMN]', this row will move position as soon as you select another row`,
                    },
                },
                filters: {
                    button: t`Filters`,
                    buttons: {
                        addNewFilter: t`Add new filter`,
                        clearFilters: t`Clear all filters`,
                    },
                    comparators: {
                        contains: t`Contains`,
                        doesNotContain: t`Does not contain`,
                        isEqualTo: t`Is`,
                        isNotEqualTo: t`Is not`,
                        isGreaterThan: t`Is greater than`,
                        isLessThan: t`Is less than`,
                        isBetween: t`Is between`,
                        isOneOf: t`Is one of`,
                        isNoneOf: t`Is none of`,
                        isAllOf: t`Is all of`,
                        isEmpty: t`Is empty`,
                        isNotEmpty: t`Is not empty`,
                    },
                    conditions: {
                        and: t`And`,
                        where: t`Where`,
                    },
                    emptyFilter: {
                        condition: t`Condition`,
                        value: t`Value`,
                    },
                    tooltip: t`Apply filters`,
                    total: t`Viewing [CURRENT] of [TOTAL]`,
                },
                footer: {
                    summary: {
                        records: t`Records:`,
                        selected: t`Records selected:`,
                    },
                },
                rowDensity: {
                    tooltip: t`Row density`,
                    compact: t`Compact`,
                    normal: t`Normal`,
                    comfortable: t`Comfortable`,
                    spacious: t`Spacious`,
                },
                search: {
                    placeholder: t`Search...`,
                },
                shortcuts: {
                    tooltip: t`View keyboard shortcuts`,
                    search: t`Search`,
                    filter: t`Filter`,
                    previousRow: t`Previous row`,
                    nextRow: t`Next row`,
                    editCell: t`Edit cell`,
                    previousColumn: t`Previous column`,
                    nextColumn: t`Next column`,
                    rowClick: t`Activate current row`,
                    selectRow: t`Select current row`,
                    selectAllRows: t`Select all rows`,
                    expandRow: t`Expand current row`,
                    collapseRow: t`Collapse current row`,
                },
            },
            table3: {
                columns: {
                    actions: {
                        tooltip: t`Other actions`,
                    },
                    drag: {
                        tooltip: t`Drag row`,
                    },
                    expansion: {
                        collapse: t`Collapse row`,
                        collapseAll: t`Collapse all rows`,
                        expand: t`Expand row`,
                        expandAll: t`Expand all rows`,
                    },
                    menu: {
                        freezeFirstColumn: t`Freeze the first column`,
                        freezeUptoColumn: t`Freeze first [TOTAL] columns`,
                        gotoRow: 'Go to',
                        hideColumn: t`Hide column`,
                        unfreezeColumns: t`Unfreeze all columns`,
                    },
                    resize: {
                        tooltip: t`Resize column`,
                    },
                    select: {
                        deselect: t`Deselect row`,
                        deselectAll: t`Deselect all rows`,
                        select: t`Select row`,
                        selectAll: t`Select all rows`,
                    },
                },
                columnSettings: {
                    search: t`Search column...`,
                    tooltip: t`Column settings`,
                    button: t`Columns`,
                    noResults: t`No results`,
                },
                editing: {
                    actions: {
                        tooltip: t`Row actions`,
                        save: t`Save`,
                        clear: t`Clear changes`,
                        exit: t`Exit edit mode`,
                    },
                    buttons: {
                        edit: {
                            text: t`Edit`,
                            tooltip: t`Edit`,
                        },
                    },
                    rowIndicator: {
                        rowWillMove: t`Row will move due to sorting`,
                        rowWillBeHidden: t`Row will hide due to filtering`,
                        rowWillMoveReasonSearch: t`Due to a search filter applied to this table, this row will be hidden as soon as you select another row`,
                        rowWillMoveReasonFilter: t`Due to a filter applied to the column '[COLUMN]', this row will be hidden as soon as you select another row`,
                        rowWillMoveReasonSorting: t`Due to sorting applied to the column '[COLUMN]', this row will move position as soon as you select another row`,
                    },
                    saving: {
                        progress: t`Saving...`,
                        complete: t`Saved`,
                    },
                },
                filters: {
                    button: t`Filters`,
                    buttons: {
                        addFilter: t`Add new filter`,
                        clearFilters: t`Clear all filters`,
                    },
                    comparators: {
                        contains: t`Contains`,
                        doesNotContain: t`Does not contain`,
                        isEqualTo: t`Is`,
                        isNotEqualTo: t`Is not`,
                        isGreaterThan: t`Is greater than`,
                        isLessThan: t`Is less than`,
                        isBetween: t`Is between`,
                        isEmpty: t`Is empty`,
                        isNotEmpty: t`Is not empty`,
                        isBefore: t`Is before`,
                        isAfter: t`Is after`,
                        isOnOrBefore: t`Is on or before`,
                        isOnOrAfter: t`Is on or after`,
                        isLessThanOrEqualTo: t`Is less than or equal to`,
                        isGreaterThanOrEqualTo: t`Is greater than or equal to`,
                        hasAnyOf: t`Has any of`,
                        hasAllOf: t`Has all of`,
                        hasNoneOf: t`Has none of`,
                    },
                    conditions: {
                        and: t`And`,
                        where: t`Where`,
                    },
                    emptyFilter: {
                        condition: t`Condition`,
                        value: t`Value`,
                    },
                    tooltip: t`Apply filters`,
                    total: t`Viewing [CURRENT] of [TOTAL]`,
                },
                fontSize: {
                    tooltip: t`Font size`,
                    sizes: {
                        small: t`Small`,
                        medium: t`Medium`,
                        large: t`Large`,
                    },
                },
                footer: {
                    summary: {
                        count: t`of`,
                        records: t`Records:`,
                        selected: t`Records selected:`,
                    },
                },
                print: {
                    error: t`An error occurred while creating a print preview`,
                    loading: t`Creating a print preview`,
                    tooltip: t`Print`,
                    warningDialog: {
                        title: t`Extra loading time`,
                        description: t`Please note that printing a large amount of data may result in a slightly longer waiting time due to data loading.`,
                        checkboxVisibilityLabel: t`Don't show again`,
                        cancelButtonText: t`Cancel`,
                        printButtonText: t`Print`,
                    },
                },
                rowHeight: {
                    tooltip: t`Row height`,
                    sizes: {
                        short: t`Short`,
                        medium: t`Medium`,
                        tall: t`Tall`,
                        extraTall: t`Extra tall`,
                    },
                },
                search: {
                    excludeUnmatchedResults: t`Hide unmatched records`,
                    placeholder: t`Search...`,
                },
                otherOptions: {
                    tooltip: t`Other options`,
                },
            },
            toasts: {
                dismiss: t`Dismiss`,
            },
            tour: {
                back: t`Back`,
                close: t`Close`,
                last: t`Done`,
                next: t`Next`,
                open: t`Open`,
                skip: t`Skip`,
            },
        },
    };
};
